import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  type Ref,
  ref,
  watch,
} from 'vue';

/**
 * Positions a dropdown menu with `position: fixed` relative to its trigger.
 * Used with `<Teleport to="body">` so menus aren't clipped by toolbar overflow.
 */
export function useFixedDropdownMenu(
  open: Ref<boolean>,
  triggerRef: Ref<HTMLElement | undefined>
) {
  const position = ref({ top: 0, left: 0 });

  async function updatePosition() {
    await nextTick();
    const trigger = triggerRef.value;
    if (!trigger || !open.value) return;
    const rect = trigger.getBoundingClientRect();
    position.value = {
      top: rect.bottom + 6,
      left: rect.left,
    };
  }

  watch(open, (isOpen) => {
    if (isOpen) void updatePosition();
  });

  function onLayoutChange() {
    if (open.value) void updatePosition();
  }

  onMounted(() => {
    window.addEventListener('resize', onLayoutChange);
    window.addEventListener('scroll', onLayoutChange, true);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('resize', onLayoutChange);
    window.removeEventListener('scroll', onLayoutChange, true);
  });

  const menuStyle = computed(() => ({
    position: 'fixed' as const,
    top: `${position.value.top}px`,
    left: `${position.value.left}px`,
    zIndex: 9999,
  }));

  return { menuStyle, updatePosition };
}
